const VERSION_PATTERN = /^[a-f0-9]{64}$/;
export const VERSION_CHECK_INTERVAL = 60_000;
export const RELOAD_RECORD_KEY = 'vitrine:last-version-reload';

export interface ReloadRecord { from: string; to: string; at: number }

interface VersionMonitorOptions {
  currentVersion: string;
  fetchVersion: () => Promise<unknown>;
  isActive: () => boolean;
  canReload: () => boolean;
  reload: () => void;
  onPending: (pending: boolean) => void;
  readRecord: () => ReloadRecord | null;
  writeRecord: (record: ReloadRecord) => boolean;
  now?: () => number;
}

export function isBuildVersion(value: unknown): value is string {
  return typeof value === 'string' && VERSION_PATTERN.test(value);
}

// Independent of React/DOM so deployment races, offline failures and reload
// protection can be checked without reloading a real browser.
export function createVersionMonitor(options: VersionMonitorOptions) {
  let busy = false;
  let stopped = false;
  let reloading = false;
  const now = options.now ?? Date.now;
  return {
    async check(force = false): Promise<void> {
      if (stopped || busy || reloading || !options.isActive() || !isBuildVersion(options.currentVersion)) return;
      busy = true;
      try {
        const version = await options.fetchVersion();
        if (stopped || !options.isActive() || !isBuildVersion(version)) return;
        if (version === options.currentVersion) { options.onPending(false); return; }
        const record = options.readRecord();
        // A proxy serving the old document must never cause a reload loop.
        // Also allow deployment rollouts/rollbacks to settle for one minute.
        const blocked = record && ((record.from === options.currentVersion && record.to === version)
          || (now() - record.at >= 0 && now() - record.at < VERSION_CHECK_INTERVAL));
        if (!force && (!options.canReload() || blocked)) { options.onPending(true); return; }
        const remembered = options.writeRecord({ from: options.currentVersion, to: version, at: now() });
        if (!remembered && !force) { options.onPending(true); return; }
        reloading = true;
        options.reload();
      } catch {
        // A failed/offline/protected request is not proof of a new deployment.
        // Keep the current working page and try again on the next check.
      } finally { busy = false; }
    },
    stop() { stopped = true; },
  };
}

export function readReloadRecord(value: string | null): ReloadRecord | null {
  try {
    const parsed: unknown = value ? JSON.parse(value) : null;
    if (!parsed || typeof parsed !== 'object') return null;
    const record = parsed as Partial<ReloadRecord>;
    return isBuildVersion(record.from) && isBuildVersion(record.to) && typeof record.at === 'number' && Number.isFinite(record.at)
      ? record as ReloadRecord : null;
  } catch { return null; }
}
